import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption, mapToDropdownOptionArrayWithSep } from 'components/final-form/dropdownUtils';
import Radio from 'components/final-form/Radio';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import OrderAdministrationModal from 'components/medicaments/orderadministration/OrderAdministrationModal';
import OrderPositionsModal from 'components/medicaments/orderadministration/OrderPositionsModal';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormState } from 'final-form';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Icon, Loader, Message, Modal, RadioProps } from 'semantic-ui-react';
import { searchSelectedMedicaments } from 'services/medicamentServices';
import { findOrderAdministrationById, searchOrderAdministration } from 'services/orderAdministrationServices';
import { getAllSuppliers } from 'services/suppliersService';
import { debounce } from 'ts-debounce';
import { OrderAdministrationOrderStatus, OrderAdministrationSearchBy } from 'ts-types/api.enums';
import {
  MedicamentDto,
  OrderAdministrationDto,
  OrderAdministrationSearchRequest,
  SupplierDto,
  UpsertOrderAdministrationDto
} from 'ts-types/api.types';

const initialSearchValues: Partial<OrderAdministrationSearchRequest> = {
  searchBy: OrderAdministrationSearchBy.SUPPLIER,
  supplierId: undefined,
  fromOrderDate: moment(Date.now()).format("YYYY-MM-DD"),
  toOrderDate: moment(Date.now()).format("YYYY-MM-DD")
};

interface OrderAdministrationFieldProps extends FormState {
  values: Partial<OrderAdministrationSearchRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  foundOrderData: Array<OrderAdministrationDto>;
  suppliers: Array<SupplierDto>;
  supplierOptions: Array<DropdownOption>;
  order?: UpsertOrderAdministrationDto;
  orderId?: number;
  medicaments: Array<MedicamentDto>;
  addOrderModalOpen: boolean;
  orderPositionsModalOpen: boolean;
  pageDataLoaded: boolean;
  errorMessage?: String;
  cancelTokenSource: CancelTokenSource;
}

class OrderAdministrationView extends Component<Props, State> {

  private mounted: boolean = false;
  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();

  constructor(props: Props) {
    super(props);

    const {radioMap} = this;
    const {getMessage} = props;
    const cancelTokenSource = axios.CancelToken.source();

    const searchByValues = Object.values(OrderAdministrationSearchBy);
    const radioDefinitions = generateRadioButtonOptionsFromEnum(searchByValues, "orderAdministration.searchBy", getMessage);

    radioDefinitions.forEach((radioDefinition, index) => {
      radioMap.set(searchByValues[index], radioDefinition);
    });

    this.state = {
      foundOrderData: [],
      suppliers: [],
      supplierOptions: [],
      order: undefined,
      orderId: undefined,
      medicaments: [],
      addOrderModalOpen: false,
      orderPositionsModalOpen: false,
      pageDataLoaded: false,
      cancelTokenSource
    };

    this.initialFormSearch();
  }

  initialFormSearch = async () => {
    const {cancelTokenSource} = this.state;

    const suppliersResponse = await getAllSuppliers(cancelTokenSource)
    const medicamentsReponse = await searchSelectedMedicaments("", cancelTokenSource)

    try {
      initialSearchValues.supplierId = suppliersResponse[0].id

      this.setState({
        suppliers: suppliersResponse,
        supplierOptions: mapToDropdownOptionArrayWithSep(
            suppliersResponse, ["description"], "id"),
        medicaments: medicamentsReponse
      });
    } catch (e) {
      this.handleError(e.response.data);
    }

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  showModal = (orderId?: number, order?: UpsertOrderAdministrationDto) => {
    this.setState(({}: State) => {
      return {
        addOrderModalOpen: true,
        order: order,
        orderId: orderId
      };
    });
  };

  showOrderPositionsModal = (orderId?: number, order?: UpsertOrderAdministrationDto) => {
    this.setState(({}: State) => {
      return {
        orderPositionsModalOpen: true,
        order: order,
        orderId: orderId
      };
    });
  };

  onOrderActionClick = async (orderId?: number, status?: OrderAdministrationOrderStatus) => {

    const {cancelTokenSource} = this.state;

    if (orderId) {
      try {
        const orderAdministration = await findOrderAdministrationById(orderId, cancelTokenSource);

        if (status && OrderAdministrationOrderStatus.CREATED === status) {
          this.showModal(orderId, orderAdministration);
        } else {
          this.showOrderPositionsModal(orderId, orderAdministration)
        }
      } catch (e) {
        this.handleError(e.response.data)
      }
    }
  };

  hideModal = () => {
    this.setState(({}: State) => ({
      addOrderModalOpen: false,
      orderPositionsModalOpen: false,
      order: undefined,
      orderId: undefined
    }));

    this.initialFormSearch();
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: string[] = [];

    if (knownErrors.includes(errorCode)) {
      this.setErrorMessage(getMessage(`error.${errorCode}`))
    } else {
      this.setErrorMessage(getMessage('error.general'))
    }
  }

  setErrorMessage = (error?: String) => {
    this.setState({
      errorMessage: error
    })
  }

  fetchOrderData = (values: any) => {

    const {cancelTokenSource} = this.state;
    this.setState({pageDataLoaded: false});

    const onFinally = () => {
      this.setState({
        pageDataLoaded: true
      })
    };

    searchOrderAdministration(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundOrderData: response
            })
        )
        .catch((error) => this.handleError(error.response.data))
        .finally(onFinally);
  }

  handleChange = debounce(({values}: OrderAdministrationFieldProps): void => {
    if (!this.mounted) {
      return;
    }
    this.fetchOrderData(values);
  }, 300);

  render(): React.ReactNode {
    const {pageDataLoaded} = this.state;
    return (
        <PraxStyledSegment>

          <div>
            {this.renderSearchSegment()}
          </div>

          <StyledViewTableRow>
            {!pageDataLoaded
                ? this.renderLoader()
                : this.renderOrdersTable()}
          </StyledViewTableRow>

          {this.renderAddOrderModal()}
          {this.renderOrderPositionsModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{...initialSearchValues}}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {radioMap} = this;
    const {getMessage} = this.props;
    const {errorMessage, foundOrderData, supplierOptions} = this.state;

    const numResults = foundOrderData.length;

    const supplierOption = radioMap.get(OrderAdministrationSearchBy.SUPPLIER);
    const dateRangeOption = radioMap.get(OrderAdministrationSearchBy.DATE_RANGE);

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={2}>
              <Field
                  name="searchBy"
                  component={Radio}
                  radioDefinition={supplierOption}
              />
            </Grid.Column>
            <Grid.Column width={2}>
              <Field
                  name="searchBy"
                  component={Radio}
                  radioDefinition={dateRangeOption}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={2}>
              <Field
                  name="supplierId"
                  component={Select}
                  options={supplierOptions}
                  fluid
              />
            </Grid.Column>
            <Grid.Column width={1}>
              <Field
                  name="fromOrderDate"
                  component={DatePicker}
                  fluid
              />
            </Grid.Column>
            <Grid.Column width={1}>
              <Field
                  name="toOrderDate"
                  component={DatePicker}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <Button onClick={() => this.showModal()}>
                {getMessage("button.add")}
              </Button>
            </Grid.Column>
          </Grid.Row>

          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative onDismiss={() => this.setErrorMessage()}>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">
            {getMessage("orderAdministration.loading")}
          </Loader>
        </Dimmer>
    );
  };

  renderOrdersTable = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundOrderData} = this.state;

    return (
        <VirtualizedTable
            id="orderAdministrationMainTable"
            rowCount={foundOrderData.length}
            rowGetter={this.orderAdministrationRowGetter}
            rowRenderer={this.foundOrderAdministrationRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: getMessage("orderAdministration.orderDate"),
                dataKey: "orderDate",
                cellRenderer: this.orderDateCellRenderer
              },
              {
                width: 250,
                flexGrow: 1,
                label: getMessage("orderAdministration.supplier"),
                dataKey: "supplierId",
                cellRenderer: this.supplierCellRenderer
              },
              {
                width: 300,
                label: getMessage("actions"),
                dataKey: "id",
                cellRenderer: this.actionsCellRenderer
              },
              {
                width: 200,
                label: getMessage("orderAdministration.status"),
                dataKey: "orderStatus"
              }
            ]}
        />
    );
  };

  supplierCellRenderer = (data: any) => {

    const {cellData} = data;
    const {suppliers} = this.state;

    if (suppliers && suppliers.length > 0) {
      return suppliers.filter(s => s.id === cellData)[0].description;
    }

    return "";
  };

  orderDateCellRenderer = (data: any) => {

    const {cellData} = data;

    if (moment(cellData).isValid()) {
      return moment(cellData).format("DD.MM.YYYY")
    }

    return "";
  };

  actionsCellRenderer = (data: any) => {

    const {rowData} = data;

    if (rowData) {
      return (<Icon name="first order"
                    color="red"
                    onClick={() => this.onOrderActionClick(rowData.id, rowData.orderStatus)}
          />
      )
    }
  };

  orderAdministrationRowGetter = ({index}: any) => {
    const {foundOrderData} = this.state;

    Object.assign(foundOrderData[index], {index: index + 1});

    return foundOrderData[index];
  };

  foundOrderAdministrationRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderAddOrderModal = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {addOrderModalOpen, medicaments, order, orderId} = this.state;

    return (
        <Modal open={addOrderModalOpen}
               closeOnDimmerClick={false}
               onClose={this.hideModal}
               style={{width: '1200px'}}
        >
          <Modal.Header>{getMessage("orderAdministration.title")}</Modal.Header>
          <FormModalContentContainer>
            <OrderAdministrationModal
                allMedicaments={medicaments}
                order={order}
                orderId={orderId}
                onCancel={() => this.hideModal()}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

  renderOrderPositionsModal = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {orderPositionsModalOpen, medicaments, order, orderId} = this.state;

    return (
        <Modal open={orderPositionsModalOpen}
               closeOnDimmerClick={false}
               onClose={() => this.hideModal()}
               style={{width: 'max-content'}}
        >
          <Modal.Header>{getMessage("orderAdministration.title")}</Modal.Header>
          <FormModalContentContainer>
            <OrderPositionsModal
                order={order}
                orderId={orderId}
                medicaments={medicaments}
                onCancel={() => this.hideModal()}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(withAuthContext(OrderAdministrationView));
